import { BATCH_INTERVAL_MS, LUA_DESKTOP_ANIMATION_DURATION_MS, LUA_MOBILE_ANIMATION_DURATION_MS } from 'constants.js'
import { isMobile, rand } from 'utils'
import _ from 'lodash'
import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'redux/store'
import { Lua } from 'data/LiveUserActions'

export const LUA_POOL_SIZE = 80

export const LUA_ANIMATION_DURATION_MS = isMobile()
    ? LUA_MOBILE_ANIMATION_DURATION_MS
    : LUA_DESKTOP_ANIMATION_DURATION_MS

export const MIN_LUA_DISPLAY_INTERVAL_MS = 150

export interface UserCustomLua {
    luaPassTierId: string
    luaPassTierImgUrl: string
}

export interface LuaPayload {
    emoji: Emoji
    isUnicode: boolean
}

interface LiveUserActionsState {
    nextSlot: number
    active: boolean
    list: Lua[]
    userCustomLuas: UserCustomLua[]
}

type Emoji = string | null | undefined

export const initialState: LiveUserActionsState = {
    nextSlot: 0,
    active: false,
    list: _.range(0, LUA_POOL_SIZE).map((_, id) => ({
        id,
        endsAt: 0,
        delay: rand(0, 1000),
        offset: rand(0, 60),
        direction: ~~rand(0, 2),
    })),
    userCustomLuas: [],
}

const addLua = (
    { emoji, isUnicode, delay }: { emoji: Emoji; isUnicode: boolean; delay: number },
    state: LiveUserActionsState,
) => {
    const nextSlotLua = state.list[state.nextSlot]

    if (nextSlotLua && nextSlotLua.endsAt < Date.now()) {
        nextSlotLua.emoji = emoji
        nextSlotLua.isUnicode = isUnicode
        nextSlotLua.endsAt = Date.now() + LUA_ANIMATION_DURATION_MS + delay
        nextSlotLua.delay = delay
        state.nextSlot = (state.nextSlot + 1) % LUA_POOL_SIZE
    }
}

const luaSlice = createSlice({
    name: 'lua',
    initialState,
    reducers: {
        resetState: _ => initialState,
        setLuasActive: (state, { payload: isActive }: PayloadAction<boolean>) => {
            state.active = isActive
        },
        luaBatchReceived: (state, { payload: luas }: PayloadAction<Lua[]>) => {
            const currentBatchSize = luas.length
            const luaDisplayIntervalMs = Math.max(BATCH_INTERVAL_MS / currentBatchSize, MIN_LUA_DISPLAY_INTERVAL_MS)

            for (
                let i = 0, luaDelay = 0;
                i < currentBatchSize && luaDelay < BATCH_INTERVAL_MS;
                i++, luaDelay = luaDisplayIntervalMs * i
            ) {
                const lua = luas[i]
                addLua({ emoji: lua.emoji, isUnicode: lua.isUnicode, delay: luaDelay }, state)
            }
        },
        luaReceived: (state, { payload }: PayloadAction<LuaPayload>) => {
            addLua({ emoji: payload.emoji, isUnicode: payload.isUnicode, delay: 0 }, state)
        },
        luaRemoved: (state, { payload: lua }: PayloadAction<Lua>) => {
            const removedLua = state.list[lua.id]

            removedLua.emoji = null
            removedLua.delay = 0
            removedLua.endsAt = 0
        },
        setUserCustomLuas: (state, { payload: userCustomLuas }: PayloadAction<UserCustomLua[]>) => {
            state.userCustomLuas = userCustomLuas
        },
    },
})

const { actions, reducer } = luaSlice
export default reducer

export const getLiveUserActionsState = (store: RootState) => store.liveUserActions
export const getIsActiveLUAs = createSelector(getLiveUserActionsState, luas => luas.active)
export const getLUAs = createSelector(getLiveUserActionsState, getIsActiveLUAs, (luas, active) =>
    active ? Object.values(luas.list) : [],
)
export const getUserCustomLuas = createSelector(getLiveUserActionsState, luas => luas.userCustomLuas)

export const { resetState, luaReceived, luaBatchReceived, luaRemoved, setLuasActive, setUserCustomLuas } = actions
